import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowUpRight } from 'lucide-react';
import { SectionHeading, EmptyState, StatTile } from '../components/mailer/atoms';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mailer/dashboard`;

// Lifecycle milestone statuses, matched against backend `is_*` flags. These
// are the three the team tracks for the mailer funnel: who hasn't been picked
// up, who's actively in underwriting, and who closed.
const STATUS_OPTIONS = [
  { value: 'all',        label: 'All Status' },
  { value: 'unclaimed',  label: 'Unclaimed'  },
  { value: 'submitted',  label: 'Submitted'  },
  { value: 'funded',     label: 'Funded'     },
];

function StatusBadge({ isClaimed, isSubmitted, isFunded }) {
  if (isFunded) return <span className="inline-flex items-center px-2 py-0.5 text-[9.5px] uppercase tracking-wider font-bold rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Funded</span>;
  if (isSubmitted) return <span className="inline-flex items-center px-2 py-0.5 text-[9.5px] uppercase tracking-wider font-bold rounded bg-sky-50 text-sky-700 border border-sky-200">Submitted</span>;
  if (!isClaimed) return <span className="inline-flex items-center px-2 py-0.5 text-[9.5px] uppercase tracking-wider font-bold rounded bg-neutral-100 text-neutral-600 border hairline">Unclaimed</span>;
  return <span className="inline-flex items-center px-2 py-0.5 text-[9.5px] uppercase tracking-wider font-bold rounded bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border border-[hsl(var(--kf-gold))]/40">Claimed</span>;
}

export default function MailerDrilldown() {
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [filters, setFilters] = useState(() => ({
    campaign: searchParams.get('campaign') || 'all',
    state: searchParams.get('state') || 'all',
    status: searchParams.get('status') || 'all',
  }));

  useEffect(() => {
    axios.get(`${API}/campaigns-list`).then((r) => setCampaigns(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.campaign !== 'all') params.campaign = filters.campaign;
    if (filters.state !== 'all') params.state = filters.state;
    if (filters.status !== 'all') params.status = filters.status;
    axios.get(`${API}/drilldown`, { params }).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [filters]);

  const states = useMemo(() => {
    const s = new Set(rows.map((r) => r.state).filter(Boolean));
    return Array.from(s).sort();
  }, [rows]);

  const summary = useMemo(() => ({
    total: rows.length,
    unclaimed: rows.filter((r) => !r.is_claimed).length,
    submitted: rows.filter((r) => r.is_submitted).length,
    funded: rows.filter((r) => r.is_funded).length,
  }), [rows]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto space-y-6" data-testid="mailer-drilldown-page">
      <SectionHeading>Record-Level Drill-Down</SectionHeading>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile label="Rows" value={summary.total} testId="drilldown-rows" />
        <StatTile label="Unclaimed" value={summary.unclaimed} testId="drilldown-unclaimed" />
        <StatTile label="Submitted" value={summary.submitted} testId="drilldown-submitted" />
        <StatTile label="Funded" value={summary.funded} testId="drilldown-funded" tone="positive" />
      </div>

      <div className="rounded-2xl border hairline bg-white p-4 flex flex-wrap items-center gap-3">
        <select
          data-testid="drilldown-filter-campaign"
          value={filters.campaign}
          onChange={(e) => set('campaign', e.target.value)}
          className="h-9 px-3 rounded-md bg-white border hairline text-[12.5px] font-semibold text-neutral-900 focus:outline-none focus:border-neutral-900"
        >
          <option value="all">All Campaigns</option>
          {campaigns.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          data-testid="drilldown-filter-state"
          value={filters.state}
          onChange={(e) => set('state', e.target.value)}
          className="h-9 px-3 rounded-md bg-white border hairline text-[12.5px] font-semibold text-neutral-900 focus:outline-none focus:border-neutral-900"
        >
          <option value="all">All States</option>
          {states.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          data-testid="drilldown-filter-status"
          value={filters.status}
          onChange={(e) => set('status', e.target.value)}
          className="h-9 px-3 rounded-md bg-white border hairline text-[12.5px] font-semibold text-neutral-900 focus:outline-none focus:border-neutral-900"
        >
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="h-64 rounded-2xl border hairline bg-white animate-pulse" />
      ) : rows.length === 0 ? (
        <EmptyState title="No matching rows" subtitle="Adjust filters above or upload mailer data on the Upload tab." />
      ) : (
        <div className="rounded-2xl border hairline bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid="drilldown-table">
              <thead className="bg-neutral-50/60 border-b hairline">
                <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
                  <th className="px-4 py-3 w-12">#</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Tracking ID</th>
                  <th className="px-4 py-3">Campaign</th>
                  <th className="px-4 py-3">City, State</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Est. Amt</th>
                  <th className="px-4 py-3 text-right">Created</th>
                  <th className="px-4 py-3 text-right">Open</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {rows.map((r, i) => (
                  <tr key={r.id} className="hover:bg-neutral-50/60" data-testid={`drilldown-row-${i}`}>
                    <td className="px-4 py-3 text-neutral-400 mono text-[10.5px]">{i + 1}</td>
                    <td className="px-4 py-3 font-semibold text-neutral-900">{r.name || <span className="italic text-neutral-400">—</span>}</td>
                    <td className="px-4 py-3 display tracking-tight text-neutral-700">{r.phone || '—'}</td>
                    <td className="px-4 py-3 mono text-[11px] text-[hsl(var(--kf-gold-deep))] font-bold">{r.reference_id || '—'}</td>
                    <td className="px-4 py-3 text-neutral-700">{r.campaign}</td>
                    <td className="px-4 py-3 text-neutral-600">{[r.city, r.state].filter(Boolean).join(', ') || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge isClaimed={r.is_claimed} isSubmitted={r.is_submitted} isFunded={r.is_funded} /></td>
                    <td className="px-4 py-3 text-right display tracking-tight font-semibold text-neutral-900">{r.est_amount ? `$${Number(r.est_amount).toLocaleString('en-US')}` : '—'}</td>
                    <td className={cn('px-4 py-3 text-right display tracking-tight text-neutral-500 text-[11.5px]')}>{r.created_at || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/leads/${r.id}?scope=global`}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-md border hairline text-[11px] font-bold text-neutral-700 hover:border-neutral-400 hover:text-neutral-900"
                        data-testid={`drilldown-open-${r.id}`}
                      >
                        Open <ArrowUpRight className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
